import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, userService } from '../services/api';
import AbstractAIBackground from '../assets/abstract-ai-bg';
import './AuthPages.css';
import { Check, Eye, EyeOff, Mail, Lock, User, ArrowRight, Home, X, Info } from 'react-feather';

const Register = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [notification, setNotification] = useState({ show: false, message: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({
    length: true,
    uppercase: true,
    lowercase: true,
    number: true,
    special: true
  });
  const navigate = useNavigate();

  // Check for mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 900);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.keyCode === 27) { // ESC key
        navigate('/');
      }
    };
    
    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [navigate]);

  useEffect(() => {
    validatePassword(password);
  }, [password]);

  // Auto-hide notification after 5 seconds
  useEffect(() => {
    let timer;
    if (notification.show) {
      timer = setTimeout(() => {
        setNotification({ show: false, message: '' });
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [notification.show]);

  const validatePassword = (password) => {
    const errors = {
      length: password.length < 8,
      uppercase: !/[A-Z]/.test(password),
      lowercase: !/[a-z]/.test(password),
      number: !/[0-9]/.test(password),
      special: !/[^A-Za-z0-9]/.test(password)
    };
    setPasswordErrors(errors);
    return !Object.values(errors).some(Boolean);
  };

  const handleBackdropClick = (e) => {
    if (e.target.className === 'auth-backdrop') {
      navigate('/');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setNotification({ show: false, message: '' });

    if (!validatePassword(password)) {
      setError('Please fix the password issues below');
      setLoading(false);
      return;
    }

    if (!agreeToTerms) {
      setError('You must agree to the Terms & Conditions');
      setLoading(false);
      return;
    }

    try {
      // Use the actual auth service for registration
      const response = await authService.register(username, email, password);
      
      if (response && response.token) {
        // Successfully registered and logged in
        try {
          const me = await userService.getUser();
          if (me && me.isInitialSetupCompleted === false) {
            setLoading(false);
            navigate('/initial-setup');
            return;
          }
        } catch (_) {}
        setLoading(false);
        navigate('/home');
      } else {
        throw new Error('Registration failed');
      }
    } catch (err) {
      setLoading(false);
      
      // Check if it's the email exists error
      if (err.response?.data?.includes('email already exists') || 
          err.message?.includes('email already exists') ||
          err.response?.data?.includes('User with this email already exists') ||
          err.message?.includes('User with this email already exists')) {
        // Show notification for email exists error
        setNotification({ 
          show: true, 
          message: 'User with this email already exists' 
        });
      } else {
        // Show other errors in the regular error element
        setError(err.response?.data || err.message || 'Failed to register. Please try again.');
      }
    }
  };

  const navigateToLogin = () => {
    navigate('/login');
  };

  const getPasswordStrength = () => {
    const errors = Object.values(passwordErrors);
    const passedChecks = errors.filter(error => !error).length;
    
    if (passedChecks <= 1) return { label: 'Weak', class: 'weak' };
    if (passedChecks <= 3) return { label: 'Fair', class: 'fair' };
    if (passedChecks <= 4) return { label: 'Good', class: 'good' };
    return { label: 'Strong', class: 'strong' };
  };

  // Responsive feature data
  const features = [
    {
      icon: <Check size={20} />,
      text: "Free account with core features"
    },
    {
      icon: <Check size={20} />,
      text: "Secure cloud storage for your notes"
    },
    {
      icon: <Check size={20} />,
      text: "Cancel anytime, no commitments"
    }
  ];

  // Password requirements for responsive layout
  const passwordRequirements = [
    { key: 'length', label: 'At least 8 characters', isValid: !passwordErrors.length },
    { key: 'uppercase', label: 'One uppercase letter', isValid: !passwordErrors.uppercase },
    { key: 'lowercase', label: 'One lowercase letter', isValid: !passwordErrors.lowercase },
    { key: 'number', label: 'One number', isValid: !passwordErrors.number },
    { key: 'special', label: 'One special character', isValid: !passwordErrors.special }
  ];

  return (
    <div className="auth-backdrop" onClick={handleBackdropClick}>
      {notification.show && (
        <div className="auth-notification modern">
          <div className="notification-icon">
            <Info size={20} />
          </div>
          <div className="notification-content">
            {notification.message}
          </div>
          <button 
            className="notification-close" 
            onClick={() => setNotification({ show: false, message: '' })}
            aria-label="Close notification"
          >
            <X size={18} />
          </button>
        </div>
      )}
      <div className="auth-container modern" onClick={(e) => e.stopPropagation()}>
        <div className="auth-left-panel">
          <div className="background-svg">
            <AbstractAIBackground />
          </div>
          <div className="content">
            <div className="auth-logo">
              <span>Brainy</span>
            </div>
            <button onClick={() => navigate('/')} className="back-to-site modern">
              <Home size={18} />
              <span>Back to site</span>
            </button>
            <div className="auth-tagline">
              <h2>Join Our Community</h2>
              <p>Create your account and discover a new way to organize your knowledge and ideas with AI.</p>
            </div>
            <div className="auth-features">
              {features.map((feature, index) => (
                <div key={index} className="auth-feature-card">
                  <div className="auth-feature-icon">
                    {feature.icon}
                  </div>
                  <span>{feature.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="auth-right-panel">
          <div className="auth-form-container modern">
            <div className="auth-header">
              <h1>Create your account</h1>
              <p>Join Brainy and start organizing your knowledge</p>
            </div>
            
            {error && (
              <div className="auth-error modern">
                <div className="error-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                  </svg>
                </div>
                <span>{error}</span>
              </div>
            )}
            
            <form className="auth-form modern" onSubmit={handleSubmit}>
              <div className="input-group modern">
                <label htmlFor="username">Username</label>
                <div className="input-wrapper">
                  <div className="input-icon">
                    <User size={18} />
                  </div>
                  <input
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Choose a username"
                    required
                    className="modern-input"
                    autoComplete="username"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck="false"
                  />
                </div>
              </div>
              
              <div className="input-group modern">
                <label htmlFor="email">Email address</label>
                <div className="input-wrapper">
                  <div className="input-icon">
                    <Mail size={18} />
                  </div>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    required
                    className="modern-input"
                    autoComplete="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck="false"
                  />
                </div>
              </div>
              
              <div className="input-group modern">
                <label htmlFor="password">Password</label>
                <div className="input-wrapper">
                  <div className="input-icon">
                    <Lock size={18} />
                  </div>
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create a strong password"
                    required
                    className={`modern-input ${password.length > 0 && Object.values(passwordErrors).some(Boolean) ? 'invalid-password' : ''}`}
                    autoComplete="new-password"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck="false"
                  />
                  <button 
                    type="button"
                    className="password-toggle modern"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                
                {password.length > 0 && (
                  <div className="password-requirements modern">
                    <div className="password-strength">
                      <div className="strength-label">
                        Password strength: <span className={`strength-indicator ${getPasswordStrength().class}`}>
                          {getPasswordStrength().label}
                        </span>
                      </div>
                      <div className="strength-bar">
                        <div className={`strength-fill ${getPasswordStrength().class}`}></div>
                      </div>
                    </div>
                    <div className="requirements-list">
                      {passwordRequirements.map((requirement) => (
                        <div key={requirement.key} className={`requirement ${requirement.isValid ? 'met' : 'unmet'}`}>
                          {requirement.isValid ? <Check size={14} /> : <X size={14} />}
                          <span>{requirement.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <label className="terms-agreement modern">
                <input 
                  type="checkbox" 
                  checked={agreeToTerms}
                  onChange={(e) => setAgreeToTerms(e.target.checked)}
                  required
                />
                <span className="checkmark"></span>
                <span className="label-text">
                  I agree to the <button type="button" className="link-text" onClick={() => window.open('/terms', '_blank')}>Terms & Conditions</button> and <button type="button" className="link-text" onClick={() => window.open('/privacy', '_blank')}>Privacy Policy</button>
                </span>
              </label>
              
              <button type="submit" className="auth-submit-btn modern" disabled={loading || !agreeToTerms}>
                {loading ? (
                  <>
                    <div className="auth-loading-spinner"></div>
                    <span>Creating account...</span>
                  </>
                ) : (
                  <>
                    <span>Create account</span>
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>
            
            <div className="auth-divider modern">
              <span>or</span>
            </div>
            
            <div className="login-link modern">
              <span>Already have an account?</span>
              <button onClick={navigateToLogin} className="link-button modern">
                Sign in
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register; 